import { type AppManifest, parseAppManifest, DEV_HTTP_ALLOWLIST } from '@aid/shared';

const MANIFEST_URL =
  import.meta.env.VITE_HUB_MANIFEST_URL ?? 'http://hub.localhost:5174/app-manifest.json';

const CACHE_TTL = 30_000;

const FAILURE_COOLDOWN = 5_000;

let cachedManifest: AppManifest | null = null;
let cachePromise: Promise<AppManifest | null> | null = null;
let cacheExpiresAt = 0;

function isCacheExpired(): boolean {
  return Date.now() >= cacheExpiresAt;
}

export async function fetchManifest(): Promise<AppManifest | null> {
  if (cachedManifest && !isCacheExpired()) return cachedManifest;

  if (cachedManifest && isCacheExpired()) {
    cachedManifest = null;
    cachePromise = null;
  }

  if (cachePromise && !isCacheExpired()) return cachePromise;

  cachePromise = (async () => {
    try {
      const response = await fetch(MANIFEST_URL, {
        credentials: 'omit',
      });

      if (!response.ok) {
        console.warn(`[app-switcher] Manifest fetch failed: ${response.status}`);
        cacheExpiresAt = Date.now() + FAILURE_COOLDOWN;
        return null;
      }

      const data: unknown = await response.json();

      const manifest = parseAppManifest(data, {
        allowHttpHosts: [...DEV_HTTP_ALLOWLIST],
      });

      cachedManifest = manifest;
      cacheExpiresAt = Date.now() + CACHE_TTL;
      return manifest;
    } catch (err) {
      console.warn('[app-switcher] Manifest fetch error:', err);
      cacheExpiresAt = Date.now() + FAILURE_COOLDOWN;
      return null;
    }
  })();

  return cachePromise;
}

export function getManifestCacheStatus(): { cached: boolean; expiresAt: number } {
  return {
    cached: cachedManifest !== null && !isCacheExpired(),
    expiresAt: cacheExpiresAt,
  };
}

export function navigateToApp(url: string): void {
  window.top?.location.assign(url);
}

export function clearManifestCache(): void {
  cachedManifest = null;
  cachePromise = null;
  cacheExpiresAt = 0;
}
